import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bell, Mail, Smartphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface AlertPrefs {
  in_app_enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  price_drop: boolean;
  target_price_met: boolean;
  restock: boolean;
  better_route: boolean;
}

const DEFAULT_PREFS: AlertPrefs = {
  in_app_enabled: true,
  email_enabled: false,
  push_enabled: false,
  price_drop: true,
  target_price_met: true,
  restock: false,
  better_route: false,
};

function Toggle({ enabled, onChange, disabled }: { enabled: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative w-9 h-5 rounded-full transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"} ${
        enabled ? "bg-primary" : "bg-muted/60"
      }`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${enabled ? "translate-x-4" : ""}`} />
    </button>
  );
}

export default function AlertPreferences() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<AlertPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("alert_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        setPrefs({
          in_app_enabled: data.in_app_enabled,
          email_enabled: data.email_enabled,
          push_enabled: data.push_enabled,
          price_drop: data.price_drop,
          target_price_met: data.target_price_met,
          restock: data.restock,
          better_route: data.better_route,
        });
      }
      setLoading(false);
    })();
  }, [user]);

  const save = async (update: Partial<AlertPrefs>) => {
    if (!user) return;
    const next = { ...prefs, ...update };
    setPrefs(next);

    const { data: existing } = await supabase
      .from("alert_preferences")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (existing) {
      await supabase.from("alert_preferences").update(next).eq("user_id", user.id);
    } else {
      await supabase.from("alert_preferences").insert({ user_id: user.id, ...next });
    }
  };

  if (!user || loading) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-border/50 bg-card p-4 mb-3">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-display font-semibold text-foreground">Alert preferences</span>
      </div>

      {/* Delivery channels */}
      <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-body mb-2">Channels</p>
      <div className="space-y-2.5 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-body text-foreground">In-app alerts</span>
          </div>
          <Toggle enabled={prefs.in_app_enabled} onChange={() => save({ in_app_enabled: !prefs.in_app_enabled })} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-muted-foreground/50" />
            <span className="text-xs font-body text-muted-foreground">Email alerts</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground/60 font-body">Coming soon</span>
          </div>
          <Toggle enabled={false} onChange={() => {}} disabled />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="w-3.5 h-3.5 text-muted-foreground/50" />
            <span className="text-xs font-body text-muted-foreground">Push notifications</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground/60 font-body">Coming soon</span>
          </div>
          <Toggle enabled={false} onChange={() => {}} disabled />
        </div>
      </div>

      {/* Alert types */}
      <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-body mb-2">Alert types</p>
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-body text-foreground">Price drops</span>
          <Toggle enabled={prefs.price_drop} onChange={() => save({ price_drop: !prefs.price_drop })} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-body text-foreground">Target price reached</span>
          <Toggle enabled={prefs.target_price_met} onChange={() => save({ target_price_met: !prefs.target_price_met })} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-body text-foreground">Restocked items</span>
          <Toggle enabled={prefs.restock} onChange={() => save({ restock: !prefs.restock })} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-body text-foreground">Better route found</span>
          <Toggle enabled={prefs.better_route} onChange={() => save({ better_route: !prefs.better_route })} />
        </div>
      </div>
    </motion.div>
  );
}
